/**
 * Return a normalized string when the source value is present.
 *
 * @param {unknown} value
 * @param {string} fallback
 * @returns {string}
 */
function get_text(value, fallback = '') {
  const normalized_value = typeof value === 'string' ? value.trim() : '';
  const result = normalized_value || fallback;
  return result;
}

/**
 * Return the first non-empty text value from a document row.
 *
 * @param {object} row
 * @param {string[]} field_names
 * @param {string} fallback
 * @returns {string}
 */
function pick_text(row, field_names, fallback = '') {
  const safe_row = row || {};
  const matched_value = field_names.find((field_name) => {
    const field_value = Reflect.get(safe_row, field_name);
    return typeof field_value === 'string' && field_value.trim();
  });
  const result = matched_value
    ? get_text(Reflect.get(safe_row, matched_value), fallback)
    : fallback;
  return result;
}

/**
 * Build a fallback file name from a file path-like value.
 *
 * @param {object} row
 * @returns {string}
 */
function get_fallback_file_name(row) {
  const file_url = pick_text(row, ['file_url', 'url', 'original_url'], '');
  const file_name = file_url ? file_url.split('/').pop() : '';
  const result = get_text(file_name, 'Document');
  return result;
}

/**
 * Normalize the display name for a current document row.
 *
 * @param {object} row
 * @returns {string}
 */
function get_document_file_name(row) {
  const file_name = pick_text(
    row,
    ['file_name', 'document_name', 'name', 'question'],
    '',
  );
  const result = file_name || get_fallback_file_name(row);
  return result;
}

/**
 * Normalize the preview/download URL from the available backend fields.
 *
 * @param {object} row
 * @returns {string}
 */
function get_document_url(row) {
  const result = pick_text(
    row,
    ['presigned_url', 'preview_url', 'url', 'original_url', 'file_url'],
    '',
  );
  return result;
}

/**
 * Convert a backend document row into the card shape expected by the view.
 *
 * @param {object} row
 * @param {Function} format_date
 * @returns {object}
 */
function build_document_table_row(row, format_date) {
  const safe_row = row || {};
  const submitted_on = Reflect.get(safe_row, 'submitted_on')
    || Reflect.get(safe_row, 'date_uploaded')
    || null;
  const formatted_submitted_on = submitted_on && typeof format_date === 'function'
    ? format_date(submitted_on)
    : submitted_on;
  const result = {
    id: Reflect.get(safe_row, 'id') || null,
    file_name: get_document_file_name(safe_row),
    question_text: pick_text(safe_row, ['question', 'question_text'], ''),
    section: pick_text(safe_row, ['section'], 'Documents'),
    section_id: Reflect.get(safe_row, 'section_id') || null,
    question_id: Reflect.get(safe_row, 'question_id') || null,
    section_question_id: Reflect.get(safe_row, 'section_question_id') || null,
    presigned_url: get_document_url(safe_row),
    submitted_on,
    submitted_on_label: formatted_submitted_on,
    is_verified: Boolean(Reflect.get(safe_row, 'is_verified')),
    source: pick_text(safe_row, ['source'], ''),
    source_entity_name: pick_text(safe_row, ['source_entity_name'], ''),
    source_entity_role: pick_text(safe_row, ['source_entity_role'], ''),
    uploaded_by_name: pick_text(safe_row, ['uploaded_by_name'], ''),
  };
  return result;
}

/**
 * Return a numeric sort key for latest-first document ordering.
 *
 * @param {object} row
 * @returns {number}
 */
function get_document_timestamp(row) {
  const value = Reflect.get(row || {}, 'submitted_on') || '';
  const parsed_value = Date.parse(value);
  const result = Number.isNaN(parsed_value) ? 0 : parsed_value;
  return result;
}

/**
 * Return a numeric row ID for stable latest-first document ordering.
 *
 * @param {object} row
 * @returns {number}
 */
function get_document_id(row) {
  const id_value = Number(Reflect.get(row || {}, 'id') || 0);
  const result = Number.isNaN(id_value) ? 0 : id_value;
  return result;
}

/**
 * Return true when a normalized document row is a Z83 document.
 *
 * @param {object} row
 * @returns {boolean}
 */
function is_z83_document(row) {
  const file_name = get_text(Reflect.get(row || {}, 'file_name'), '');
  const result = file_name.toUpperCase().startsWith('Z83');
  return result;
}

/**
 * Return true when a normalized document row is a CV document.
 *
 * @param {object} row
 * @returns {boolean}
 */
function is_cv_document(row) {
  const safe_row = row || {};
  const values = [
    Reflect.get(safe_row, 'file_name'),
    Reflect.get(safe_row, 'question_text'),
    Reflect.get(safe_row, 'section'),
  ];
  const result = values.some((value) => get_text(value, '').toUpperCase().includes('CV'));
  return result;
}

/**
 * Return display priority for vault documents.
 *
 * @param {object} row
 * @returns {number}
 */
function get_document_display_priority(row) {
  if (is_cv_document(row)) {
    return 2;
  }
  const result = is_z83_document(row) ? 0 : 1;
  return result;
}

/**
 * Sort normalized document rows with CVs first, then newest others, then Z83.
 *
 * @param {object[]} rows
 * @returns {object[]}
 */
function sort_document_rows_latest_first(rows) {
  const sorted_rows = [...rows].sort((first, second) => {
    const priority_difference = get_document_display_priority(second)
      - get_document_display_priority(first);
    if (priority_difference !== 0) {
      return priority_difference;
    }
    const time_difference = get_document_timestamp(second) - get_document_timestamp(first);
    if (time_difference !== 0) {
      return time_difference;
    }
    return get_document_id(second) - get_document_id(first);
  });
  return sorted_rows;
}

/**
 * Keep only the newest Z83 while leaving other documents visible.
 *
 * @param {object[]} rows
 * @returns {object[]}
 */
function keep_latest_z83_document(rows) {
  let has_z83_document = false;
  const visible_rows = rows.filter((row) => {
    if (!is_z83_document(row)) {
      return true;
    }
    if (has_z83_document) {
      return false;
    }
    has_z83_document = true;
    return true;
  });
  return visible_rows;
}

/**
 * Return the visible history status label.
 *
 * @param {object} row
 * @returns {string}
 */
function get_history_status(row) {
  const explicit_status = pick_text(
    row,
    ['status', 'visibility_status', 'lifecycle_status'],
    '',
  );
  if (explicit_status) {
    return explicit_status;
  }
  if (Reflect.get(row || {}, 'is_current')) {
    return 'Current';
  }
  const result = Reflect.get(row || {}, 'is_superseded')
    ? 'Superseded'
    : 'History';
  return result;
}

/**
 * Convert a backend Z83 history row into the view card shape.
 *
 * @param {object} row
 * @param {Function} format_date
 * @returns {object}
 */
function build_history_table_row(row, format_date) {
  const safe_row = row || {};
  const corrected_at = Reflect.get(safe_row, 'corrected_at')
    || Reflect.get(safe_row, 'date_corrected')
    || Reflect.get(safe_row, 'updated_at')
    || null;
  const formatted_corrected_at = corrected_at && typeof format_date === 'function'
    ? format_date(corrected_at)
    : corrected_at;
  const result = {
    id: Reflect.get(safe_row, 'id') || null,
    file_name: get_document_file_name(safe_row),
    presigned_url: get_document_url(safe_row),
    status: get_history_status(safe_row),
    corrected_by_name: pick_text(
      safe_row,
      ['corrected_by_name', 'updated_by_name', 'uploaded_by_name'],
      'Not recorded',
    ),
    corrected_at: formatted_corrected_at,
    correction_reason: pick_text(
      safe_row,
      ['correction_reason', 'reason'],
      'Not recorded',
    ),
  };
  return result;
}

/**
 * Transform raw current document rows into table-safe display rows.
 *
 * @param {object[]} rows
 * @param {Function} format_date
 * @returns {object[]}
 */
export function build_document_table_rows(rows, format_date) {
  const safe_rows = Array.isArray(rows) ? rows : [];
  const mapped_rows = safe_rows.map((row) => build_document_table_row(row, format_date));
  const sorted_rows = sort_document_rows_latest_first(mapped_rows);
  const result = keep_latest_z83_document(sorted_rows);
  return result;
}

/**
 * Transform raw history rows into table-safe display rows.
 *
 * @param {object[]} rows
 * @param {Function} format_date
 * @returns {object[]}
 */
export function build_history_table_rows(rows, format_date) {
  const safe_rows = Array.isArray(rows) ? rows : [];
  const result = safe_rows.map((row) => build_history_table_row(row, format_date));
  return result;
}

/**
 * Build the query params expected by the document endpoints.
 *
 * @param {string|number|null} application_id
 * @param {string|number|null} entity_profile_id
 * @returns {object}
 */
export function build_document_request_params(application_id, entity_profile_id) {
  const params = { application_id };
  if (entity_profile_id !== null && entity_profile_id !== undefined && entity_profile_id !== '') {
    params.entity_profile_id = entity_profile_id;
  }
  const result = params;
  return result;
}

/**
 * Extract a download filename from a content-disposition header.
 *
 * @param {string} content_disposition
 * @returns {string}
 */
function get_zip_file_name(content_disposition) {
  const encoded_match = /filename\*=UTF-8''([^;]+)/i.exec(content_disposition || '');
  if (encoded_match && encoded_match[1]) {
    const decoded_file_name = decodeURIComponent(encoded_match[1]);
    return decoded_file_name;
  }
  const plain_match = /filename="?([^";]+)"?/i.exec(content_disposition || '');
  const result = plain_match && plain_match[1]
    ? plain_match[1]
    : 'application-documents.zip';
  return result;
}

/**
 * Trigger the browser download for a zip response blob.
 *
 * @param {object} response
 * @returns {void}
 */
export function save_zip_blob(response) {
  const blob = new Blob([Reflect.get(response || {}, 'data')], {
    type: 'application/zip',
  });
  const header_name = Reflect.get(response || {}, 'headers', {})
    ? Reflect.get(response.headers, 'content-disposition')
    : '';
  const file_name = get_zip_file_name(header_name);
  const blob_url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = blob_url;
  link.setAttribute('download', file_name);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blob_url);
}
